/** Schnittstelle zum Server. Der Mandant ergibt sich aus der Adresse, nicht aus einem Parameter. */

export interface Theme {
  scheme?: 'light' | 'dark';
  brand?: string;
  action?: string;
  font?: 'sans' | 'serif';
  logoUrl?: string;
}

export interface Project {
  slug: string;
  name: string;
  timezone: string;
  theme: Theme;
  /** Bietet das Projekt ein Newsletter-Häkchen an (nur mit Brevo-Liste dafür)? */
  newsletter?: boolean;
}

export interface SessionOffer {
  startsAt: string;
  kind: 'schedule' | 'next_round';
}

export interface WebinarOverview {
  slug: string;
  title: string;
  durationSeconds: number;
  sessions: SessionOffer[];
}

export type RoomState =
  | { kind: 'waiting'; secondsUntilStart: number }
  | { kind: 'playing'; positionSeconds: number }
  | { kind: 'ended' }
  | { kind: 'too_late'; elapsedSeconds: number };

export interface RoomCta {
  headline: string;
  text: string | null;
  buttonLabel: string;
  url: string;
  deadlineAt: string | null;
}

export interface RoomView {
  state: RoomState;
  webinar: { slug: string; title: string; durationSeconds: number; fullscreenEnabled: boolean };
  session: { startsAt: string };
  registration: { name: string | null };
  cta: RoomCta | null;
  ctaOffsetSeconds: number | null;
  hints: { offsetSeconds: number; text: string }[];
  video: { hlsUrl: string; posterUrl: string } | null;
  waitingImageUrl: string | null;
  cancelled: boolean;
  serverTime: string;
}

export interface ReplayView {
  state: 'unavailable' | 'not_yet' | 'available' | 'expired';
  webinar: { slug: string; title: string; durationSeconds: number };
  registration: { name: string | null };
  session: { startsAt: string };
  validUntil: string | null;
  video: { hlsUrl: string; posterUrl: string } | null;
  cta: RoomCta | null;
}

export type RegisterFailure = 'rate_limited' | 'rejected' | 'invalid_email' | 'unknown_domain' | 'unknown_webinar' | 'invalid_session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  });
  if (!res.ok) {
    let code = String(res.status);
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      code = body.error ?? body.message ?? code;
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

export async function fetchProject(): Promise<Project | null> {
  try {
    return await request<Project>('/api/project');
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export function fetchWebinar(slug: string): Promise<WebinarOverview> {
  return request(`/api/webinars/${encodeURIComponent(slug)}`);
}

export function fetchFormToken(): Promise<{ token: string }> {
  return request('/api/form-token');
}

export interface RegisterPayload {
  email: string;
  name?: string;
  sessionStartsAt?: string;
  formToken: string;
  website: string;
  newsletter?: boolean;
}

export function register(slug: string, payload: RegisterPayload): Promise<{ token: string; sessionStartsAt: string; created: boolean }> {
  return request(`/api/webinars/${encodeURIComponent(slug)}/registrations`, { method: 'POST', body: JSON.stringify(payload) });
}

export function fetchRoom(token: string): Promise<RoomView> {
  return request(`/api/room/${encodeURIComponent(token)}`);
}

export function fetchReplay(token: string): Promise<ReplayView> {
  return request(`/api/replay/${encodeURIComponent(token)}`);
}

export function heartbeat(token: string, deviceId: string, openedAt: string): Promise<{ takenOver: boolean; state: RoomState; cta: RoomCta | null; serverTime: string }> {
  return request(`/api/room/${encodeURIComponent(token)}/heartbeat`, { method: 'POST', body: JSON.stringify({ deviceId, openedAt }) });
}

export function askQuestion(token: string, text: string): Promise<{ id: string; positionSeconds: number }> {
  return request(`/api/room/${encodeURIComponent(token)}/questions`, { method: 'POST', body: JSON.stringify({ text }) });
}

export function cancelRegistration(token: string): Promise<{ cancelled: true }> {
  return request(`/api/registrations/${encodeURIComponent(token)}/cancel`, { method: 'POST' });
}

export function rescheduleRegistration(token: string, sessionStartsAt: string): Promise<{ sessionStartsAt: string }> {
  return request(`/api/registrations/${encodeURIComponent(token)}/reschedule`, { method: 'POST', body: JSON.stringify({ sessionStartsAt }) });
}

export function fetchNewsletter(token: string): Promise<{ email: string; confirmed: boolean }> {
  return request(`/api/newsletter/${encodeURIComponent(token)}`);
}

export function confirmNewsletter(token: string): Promise<{ confirmed: true }> {
  return request(`/api/newsletter/${encodeURIComponent(token)}/confirm`, { method: 'POST' });
}

export function recordCtaClick(token: string): Promise<{ positionSeconds: number }> {
  return request(`/api/room/${encodeURIComponent(token)}/cta-click`, { method: 'POST' });
}
